import { useEffect, useState, FC } from "react";
import { Formik, Form } from "formik";
import {
  Grid,
  Autocomplete,
  TextField,
  FormControl,
  Button,
  Skeleton,
} from "@mui/material";
import Box from "@mui/material/Box";
import { DataField } from "../../atoms";
import * as functions from "../../shared/functions";
import { IFormField } from "../../shared/globalTypes";
import * as constants from "../../../constants/_pickerValues";
import { useDispatch, useSelector } from "react-redux";
import { IUser } from "../../../store/groups/types";
import { getProfileDataSelector } from "../../../store/auth/selectors";
import { getGroupPendingSelector, getGroupUsersSelector } from "../../../store/groups/selectors";
import { fetchGroupUsersRequest } from "../../../store/groups/actions"
import React from "react";
import Back from "../../atoms/backButton";
import {
  postConnectionSelector,
  postConnectionPendingSelector,
} from "../../../store/dataSource/selectors";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import * as actions from "../../../store/dataSource/actions";
import * as spokeActions from "../../../store/spokes/actions";
import { ISpokePostReq } from "../../../store/spokes/types";
import {
  fetchSpokeByIdPendingSelector,
  fetchSpokebyIdSelector,
  postSpokePendingSelector,
  postSpokeSelector,
} from "../../../store/spokes/selectors";
import { fetchSpokeByIdRequest } from "../../../store/spokes/actions";
import { useParams } from "react-router-dom";
import ConnectionEXTSpoke from "../../molecules/connectionTypes/ConnectionEXTSpoke";
import ConnectionLNDSpoke from "../../molecules/connectionTypes/ConnectionLNDSpoke";
import ConnectionHistorySpoke from "../../molecules/connectionTypes/ConnectionHistorySpoke";
import { CONNECTION_TYPES } from "../../../constants/_pickerValues";
import { IDP_USERS_PRODUCTION } from "../../../constants/common";

// Form fields object

export interface ISpokeFields {
  name: IFormField;
  owner: IFormField;
  adminGroupObjectId: IFormField;
  userGroupObjectId: IFormField;
  adminGroupName: IFormField;
  userGroupName: IFormField;
  accountKey: IFormField;
  accountName: IFormField;
  lndEndpoint: IFormField;
  lndDatabase: IFormField;
  lndUsername: IFormField;
  lndPassword: IFormField;
  lndPort: IFormField;
  historyEndpoint: IFormField;
  historyDatabase: IFormField;
  historyUsername: IFormField;
  historyPassword: IFormField;
  historyPort: IFormField;
  extractSource: IFormField;
  serviceName: IFormField;
  extSecret: IFormField;
  lndSecret: IFormField;
  hstgSecret: IFormField;
}

let SpokeInitialState: ISpokeFields = {
  name: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  owner: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  adminGroupObjectId: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  adminGroupName: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  userGroupObjectId: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  userGroupName: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  accountName: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  accountKey: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  lndEndpoint: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  lndDatabase: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  lndUsername: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  lndPassword: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  historyEndpoint: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  historyDatabase: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  historyUsername: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  historyPassword: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  extractSource: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  serviceName: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  extSecret: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  hstgSecret: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  lndPort: { fieldValue: "", isError: false, errorMsg: "", isNumber: true },
  historyPort: { fieldValue: "", isError: false, errorMsg: "", isNumber: true },
  lndSecret: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
};

interface Props {
  mode?: string;
}

export const SpokeForm: FC<Props> = ({ mode = constants.MODE.add }: Props) => {
  const dispatch = useDispatch();

  //Selectors
  const userProfile = useSelector(getProfileDataSelector);
  const idpUsersProduction = useSelector(getGroupUsersSelector(IDP_USERS_PRODUCTION));
  const idpUsersProductionPending = useSelector(getGroupPendingSelector(IDP_USERS_PRODUCTION));
  const postConnection = useSelector(postConnectionSelector);
  const postConnectionPending = useSelector(postConnectionPendingSelector);
  const postSpokePending = useSelector(postSpokePendingSelector);
  const postSpoke = useSelector(postSpokeSelector);
  const getSpokeById = useSelector(fetchSpokebyIdSelector);
  const getSpokePending = useSelector(fetchSpokeByIdPendingSelector);

  const [spokeFormData, setSpokeFormData] = useState(SpokeInitialState);
  const [ownerValue, setOwnerValue] = useState("");

  const [loadIcon, setLoadIcon] = useState(false);
  const [displaySaveBtn, setDisplaySaveBtn] = useState(false);

  const [backButton, setBackButton] = useState(false);
  const { spokeId } = useParams();

  // Populate Access Controls drop down
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!idpUsersProductionPending && idpUsersProduction.length <= 0) {
      dispatch(fetchGroupUsersRequest("IDP Users Production"));
    }

    resetFields();
    setDisplaySaveBtn(false);
  }, []);

  const goBack = () => {
    resetFields();
    window.history.back();
  };

  // reset fields
  const resetFields = () => {
    if (spokeFormData.name.fieldValue === "")
      spokeFormData.name.isError = false;
    if (spokeFormData.owner.fieldValue === "")
      spokeFormData.owner.isError = false;
    setSpokeFormData(spokeFormData);
    setDisplaySaveBtn(false);
  };

  // Handling owner changes
  useEffect(() => {
    spokeFormData.owner.fieldValue = ownerValue;
    setSpokeFormData(spokeFormData);
  }, [ownerValue]);

  useEffect(() => {
    if (userProfile && mode !== "view") {
      setOwnerValue(userProfile["userPrincipalName"]);
    }
  }, [userProfile]);

  useEffect(() => {
    if (postSpoke && displaySaveBtn === true) {
      setDisplaySaveBtn(false);
      setBackButton(true);
    } else {
      if (mode === constants.MODE.add) setDisplaySaveBtn(false);
    }
  }, [postSpoke, mode]);

  // Update fieldValue when it is changed; needed to update the UI of the text fields, text areas
  const handleChange = (event: {}) => {
    const e = event as React.ChangeEvent<HTMLInputElement>;

    setSpokeFormData({
      ...spokeFormData,
      [e.target.name]:
        functions.getFormField(e.currentTarget.value),
    });
  };

  // GET Spoke details, depending on Spoke ID (when it is VIEw)

  useEffect(() => {
    if (mode === constants.MODE.view && spokeId) {
      dispatch(fetchSpokeByIdRequest(spokeId));
      setOwnerValue('');
    }
  }, [spokeId]);

  // Update spokeFormData state
  useEffect(() => {
    if (!spokeId || getSpokeById?.spokeId !== parseInt(spokeId) || mode !== constants.MODE.view) {
      setSpokeFormData(SpokeInitialState);
      return;
    }

    if (getSpokeById?.extConnection.connectionDetails) {
      try {
        let jsonString = getSpokeById?.extConnection.connectionDetails.replaceAll(`'`, `"`)
        const extConnectionDetails = JSON.parse(jsonString)
        setSpokeFormData(prevState => {
          return {
            ...prevState,
            'accountName': {
              ...prevState.accountName,
              'fieldValue': extConnectionDetails?.accountName
            }
          };
        });
      } catch (error) {
        const extConnectionDetails = getSpokeById?.extConnection.connectionDetails
        if (extConnectionDetails != null) {
          setSpokeFormData(prevState => {
            return {
              ...prevState,
              'accountName': {
                ...prevState.accountName,
                'fieldValue': extConnectionDetails
              }
            };
          });
        }
      }
    }
    if (getSpokeById?.lndConnection.connectionDetails) {
      try {
        let jsonString = getSpokeById?.lndConnection.connectionDetails.replaceAll(`'`, `"`)
        const lndConnectionDetails = JSON.parse(jsonString)
        setSpokeFormData(prevState => {
          return {
            ...prevState,
            'lndEndpoint': {
              ...prevState.lndEndpoint,
              'fieldValue': lndConnectionDetails?.lndEndpoint
            },
            'lndDatabase': {
              ...prevState.lndDatabase,
              'fieldValue': lndConnectionDetails?.lndDatabase
            },
            'lndPort': {
              ...prevState.lndPort,
              'fieldValue': lndConnectionDetails?.lndPort
            },
            'lndUsername': {
              ...prevState.lndUsername,
              'fieldValue': lndConnectionDetails?.lndUsername
            }
          };
        });
      } catch (error) {
        const lndConnectionDetails = getSpokeById?.lndConnection.connectionDetails
        if (lndConnectionDetails != null) {
          setSpokeFormData(prevState => {
            return {
              ...prevState,
              'lndEndpoint': {
                ...prevState.lndEndpoint,
                'fieldValue': ""
              },
              'lndDatabase': {
                ...prevState.lndDatabase,
                'fieldValue': ""
              },
              'lndPort': {
                ...prevState.lndPort,
                'fieldValue': ""
              },
              'lndUsername': {
                ...prevState.lndUsername,
                'fieldValue': lndConnectionDetails
              }
            };
          });
        }
      }
    }
    if (getSpokeById?.hstgConnection.connectionDetails) {
      try {
        let jsonString = getSpokeById?.hstgConnection.connectionDetails.replaceAll(`'`, `"`)
        const hstgConnectionDetails = JSON.parse(jsonString)
        if (hstgConnectionDetails?.historyEndpoint != undefined) {
          setSpokeFormData(prevState => {
            return {
              ...prevState,
              'historyEndpoint': {
                ...prevState.historyEndpoint,
                'fieldValue': hstgConnectionDetails?.historyEndpoint
              },
              'historyDatabase': {
                ...prevState.historyDatabase,
                'fieldValue': hstgConnectionDetails?.historyDatabase
              },
              'historyPort': {
                ...prevState.historyPort,
                'fieldValue': hstgConnectionDetails?.historyPort
              },
              'historyUsername': {
                ...prevState.historyUsername,
                'fieldValue': hstgConnectionDetails?.historyUsername
              }
            };
          });
        }else{
          setSpokeFormData(prevState => {
            return {
              ...prevState,
              'historyEndpoint': {
                ...prevState.historyEndpoint,
                'fieldValue': hstgConnectionDetails?.hstgEndpoint
              },
              'historyDatabase': {
                ...prevState.historyDatabase,
                'fieldValue': hstgConnectionDetails?.hstgDatabase
              },
              'historyPort': {
                ...prevState.historyPort,
                'fieldValue': hstgConnectionDetails?.hstgPort
              },
              'historyUsername': {
                ...prevState.historyUsername,
                'fieldValue': hstgConnectionDetails?.hstgUsername
              }
            };
          });
        }
      } catch (error) {
        const hstgConnectionDetails = getSpokeById?.hstgConnection.connectionDetails
        if (hstgConnectionDetails != null) {
          setSpokeFormData(prevState => {
            return {
              ...prevState,
              'historyEndpoint': {
                ...prevState.historyEndpoint,
                'fieldValue': ""
              },
              'historyDatabase': {
                ...prevState.historyDatabase,
                'fieldValue': ""
              },
              'historyPort': {
                ...prevState.historyPort,
                'fieldValue': ""
              },
              'historyUsername': {
                ...prevState.historyUsername,
                'fieldValue': hstgConnectionDetails
              }
            };
          });
        }
      }
    }

    setSpokeFormData(prevState => {
      return {
        ...prevState,
        'name': {
          ...prevState.name,
          'fieldValue': getSpokeById?.name
        },
        'owner': {
          ...prevState.owner,
          'fieldValue': getSpokeById?.owner
        },
        'extSecret': {
          ...prevState.extSecret,
          'fieldValue': getSpokeById?.extConnection.secretName
        },
        'lndSecret': {
          ...prevState.lndSecret,
          'fieldValue': getSpokeById?.lndConnection.secretName
        },
        'hstgSecret': {
          ...prevState.hstgSecret,
          'fieldValue': getSpokeById?.hstgConnection.secretName
        },
        'adminGroupName': {
          ...prevState.adminGroupName,
          'fieldValue': getSpokeById?.adminGroupName
        },
        'userGroupName': {
          ...prevState.userGroupName,
          'fieldValue': getSpokeById?.userGroupName
        }
      };
    });
  }, [getSpokeById, mode, userProfile]);

  // Test the connection, validates the required fields prior to testing
  const testConnection = () => {
    validate();

    let countTest = 0;

    dispatch(actions.postConnectionRequest(getLNDTestConnectionPayload()));
    countTest = postConnection?.succeeded ? (countTest += 1) : countTest;

    dispatch(actions.postConnectionRequest(getHSTGTestConnectionPayload()));
    countTest = postConnection?.succeeded ? (countTest += 1) : countTest;

    setLoadIcon(true);
  };

  const validate = () => {
    if (
      spokeFormData.name.fieldValue === "" ||
      spokeFormData.owner.fieldValue === "" ||
      spokeFormData.adminGroupObjectId.fieldValue === "" ||
      spokeFormData.userGroupObjectId.fieldValue === "" ||
      spokeFormData.adminGroupName.fieldValue === "" ||
      spokeFormData.userGroupName.fieldValue === "" ||
      spokeFormData.accountKey.fieldValue === "" ||
      spokeFormData.accountName.fieldValue === "" ||
      spokeFormData.lndDatabase.fieldValue === "" ||
      spokeFormData.lndEndpoint.fieldValue === "" ||
      spokeFormData.lndUsername.fieldValue === "" ||
      spokeFormData.lndPassword.fieldValue === "" ||
      spokeFormData.historyDatabase.fieldValue === "" ||
      spokeFormData.historyEndpoint.fieldValue === "" ||
      spokeFormData.historyUsername.fieldValue === "" ||
      spokeFormData.historyPassword.fieldValue === ""
    )
      alert("Required fields cannot be empty");
  };

  useEffect(() => {
    if (postConnection?.succeeded === true) {
      setDisplaySaveBtn(true);
    }
  }, [postConnection]);

  const getLNDTestConnectionPayload = () => {
    let payload = {
      type: "AzureSqlDatabase",
      appConnectionString: functions.generateConnectionString(
        CONNECTION_TYPES.lndspoke,
        constants.CONN_STRING_TYPE.app,
        spokeFormData.lndEndpoint.fieldValue,
        spokeFormData.lndDatabase.fieldValue,
        spokeFormData.lndPort.fieldValue,
        spokeFormData.lndUsername.fieldValue,
        spokeFormData.lndPassword.fieldValue
      ),
      synapseConnectionString: functions.generateConnectionString(
        CONNECTION_TYPES.lndspoke,
        constants.CONN_STRING_TYPE.synapse,
        spokeFormData.lndEndpoint.fieldValue,
        spokeFormData.lndDatabase.fieldValue,
        spokeFormData.lndPort.fieldValue,
        spokeFormData.lndUsername.fieldValue,
        spokeFormData.lndPassword.fieldValue
      ),
    };

    return payload;
  };

  const getHSTGTestConnectionPayload = () => {
    let payload = {
      type: "AzureSqlDatabase",
      appConnectionString: functions.generateConnectionString(
        CONNECTION_TYPES.hstgspoke,
        constants.CONN_STRING_TYPE.app,
        spokeFormData.historyEndpoint.fieldValue,
        spokeFormData.historyDatabase.fieldValue,
        spokeFormData.historyPort.fieldValue,
        spokeFormData.historyUsername.fieldValue,
        spokeFormData.historyPassword.fieldValue
      ),
      synapseConnectionString: functions.generateConnectionString(
        CONNECTION_TYPES.hstgspoke,
        constants.CONN_STRING_TYPE.synapse,
        spokeFormData.historyEndpoint.fieldValue,
        spokeFormData.historyDatabase.fieldValue,
        spokeFormData.historyPort.fieldValue,
        spokeFormData.historyUsername.fieldValue,
        spokeFormData.historyPassword.fieldValue
      ),
    };

    return payload;
  };

  function submitSpoke() {
    setLoadIcon(true);

    const payload: ISpokePostReq = {
      name: spokeFormData.name.fieldValue,
      owner: spokeFormData.owner.fieldValue,
      adminGroupId: spokeFormData.adminGroupObjectId.fieldValue.trim(),
      userGroupId: spokeFormData.userGroupObjectId.fieldValue.trim(),
      adminGroupName: spokeFormData.adminGroupName.fieldValue,
      userGroupName: spokeFormData.userGroupName.fieldValue,
      lndConnection: {
        secretName: functions.cleanName(
          `cs-synapse-unity-lnd${spokeFormData.name.fieldValue}`
        ),
        secrets: [
          {
            name: functions.cleanName(
              `cs-synapse-unity-lnd${spokeFormData.name.fieldValue}`
            ),
            value: functions.generateConnectionString(
              CONNECTION_TYPES.lndspoke,
              constants.CONN_STRING_TYPE.synapse,
              spokeFormData.lndEndpoint.fieldValue,
              spokeFormData.lndDatabase.fieldValue,
              spokeFormData.lndPort.fieldValue,
              spokeFormData.lndUsername.fieldValue,
              spokeFormData.lndPassword.fieldValue,
            )
          },
          {
            name: functions.cleanName(
              `cs-synapse-unity-lnd${spokeFormData.name.fieldValue}-dif`
            ),
            value: functions.generateConnectionString(
              CONNECTION_TYPES.lndspoke,
              constants.CONN_STRING_TYPE.app,
              spokeFormData.lndEndpoint.fieldValue,
              spokeFormData.lndDatabase.fieldValue,
              spokeFormData.lndPort.fieldValue,
              spokeFormData.lndUsername.fieldValue,
              spokeFormData.lndPassword.fieldValue,
            )
          },
        ],
      },
      extConnection: {
        secretName: functions.cleanName(
          `cs-datalake-storage-unity-ext${spokeFormData.name.fieldValue}`
        ),
        secrets: [
          {
            name: functions.cleanName(
              `cs-datalake-storage-unity-ext${spokeFormData.name.fieldValue}`
            ),
            value:`${spokeFormData.accountKey.fieldValue}`
          },
        ],
        connectionString: `${spokeFormData.accountName.fieldValue}:${spokeFormData.accountKey.fieldValue}`,
      },
      hstgConnection: {
        secretName: functions.cleanName(
          `cs-synapse-unity-hstg${spokeFormData.name.fieldValue}`
        ),
        secrets: [
          {
            name: functions.cleanName(
              `cs-synapse-unity-hstg${spokeFormData.name.fieldValue}`
            ),
            value: functions.generateConnectionString(
              CONNECTION_TYPES.hstgspoke,
              constants.CONN_STRING_TYPE.synapse,
              spokeFormData.historyEndpoint.fieldValue,
              spokeFormData.historyDatabase.fieldValue,
              spokeFormData.historyPort.fieldValue,
              spokeFormData.historyUsername.fieldValue,
              spokeFormData.historyPassword.fieldValue,
            )
          },
          {
            name: functions.cleanName(
              `cs-synapse-unity-hstg${spokeFormData.name.fieldValue}-dif`
            ),
            value: functions.generateConnectionString(
              CONNECTION_TYPES.hstgspoke,
              constants.CONN_STRING_TYPE.app,
              spokeFormData.historyEndpoint.fieldValue,
              spokeFormData.historyDatabase.fieldValue,
              spokeFormData.historyPort.fieldValue,
              spokeFormData.historyUsername.fieldValue,
              spokeFormData.historyPassword.fieldValue,
            )
          },
        ],
      },
    };
    dispatch(spokeActions.postSpokeRequest(payload));
  }

  return (
    <Formik initialValues={{}} onSubmit={() => { }}>
      <div>
        <Back title="Spoke List" />
        <h1 style={{ textAlign: "left" }}>{mode === constants.MODE.view ? "View Spoke" : "Register Spoke"}</h1>
        <Form>
          {/* adding skeleton screen on loading */}

          {mode === constants.MODE.view ? (
             getSpokePending ? (
              <Box>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={6}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={4}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={4}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={4}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={6}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={6}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={6}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={6}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={6}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                  <Grid item xs={6}>
                    <Skeleton animation="wave" height={60} />
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                    <DataField id="fbb9b3d6-b243-4265-bf59-701e5cbf2f3e"
                      label="Name"
                      name="name"
                      type="text"
                      value={spokeFormData.name.fieldValue}
                      disabled={true}
                      readOnly={true}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                    <DataField id="7fe5a529-a52c-4301-b2e1-286f62967028"
                      label="Owner"
                      name="owner"
                      value={spokeFormData.owner.fieldValue}
                      disabled={true}
                      readOnly={true}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                    <DataField id="21c99ac2-41cf-4be2-b0dd-fcb99ae39354"
                      label="Admin Group Name"
                      name="adminGroupName"
                      value={spokeFormData.adminGroupName.fieldValue}
                      disabled={true}
                      readOnly={true}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                    <DataField id="ad1c0980-adb9-4a1b-884d-a189d9a4cdcc"
                      label="User Group Name"
                      name="userGroupName"
                      value={spokeFormData.userGroupName.fieldValue}
                      disabled={true}
                      readOnly={true}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                    <DataField id="4b334aad-bacc-4227-995b-85864ac05d0c"
                      label="EXT Secret Name"
                      name="extSecretName"
                      value={spokeFormData.extSecret.fieldValue}
                      readOnly={true}
                      infoText="The value of secret name is auto-generated"
                      disabled={true}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                    <DataField id="870f15af-c67f-4dcf-a69c-6d619e60035c"
                      label="LND Secret Name"
                      name="lndSecretName"
                      required={true}
                      value={spokeFormData.lndSecret.fieldValue}
                      readOnly={true}
                      infoText="The value of secret name is auto-generated"
                      disabled={true}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                    <DataField id="530f5892-d37d-4da0-bdf4-b3859272bec9"
                      label="HSTG Secret Name"
                      name="hstgSecretName"
                      required={true}
                      value={spokeFormData.hstgSecret.fieldValue}
                      readOnly={true}
                      infoText="The value of secret name is auto-generated"
                      disabled={true}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex">
                    <Box
                      sx={{ border: 1, borderColor: "divider", padding: 3, width: "100%" }}
                    >
                      <h2>{constants.CONNECTION_TYPES.extSpoke}</h2>
                      <Grid container spacing={4}>
                        <Grid item xs={4}>
                          <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                            <DataField id="extAccountName"
                              label="Account Name"
                              name="accountName"
                              required={true}
                              value={spokeFormData.accountName.fieldValue}
                              readOnly={true}
                              disabled={true}
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex">
                    <Box
                      sx={{ border: 1, borderColor: "divider", padding: 3, width: "100%" }}
                    >
                      <h2>{constants.CONNECTION_TYPES.lndspoke}</h2>
                      <Grid container spacing={4}>
                        <Grid item xs={4}>
                          <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                            <DataField id="lndEndpoint"
                              label="Endpoint"
                              name="lndEndpoint"
                              required={true}
                              value={spokeFormData.lndEndpoint.fieldValue}
                              readOnly={true}
                              disabled={true}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                            <DataField id="lndDatabase"
                              label="Database"
                              name="lndDatabase"
                              required={true}
                              value={spokeFormData.lndDatabase.fieldValue}
                              readOnly={true}
                              disabled={true}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                          <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                            <DataField id="lndPort"
                              label="Port"
                              name="lndPort"
                              required={true}
                              value={spokeFormData.lndPort.fieldValue}
                              readOnly={true}
                              disabled={true}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                          <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                            <DataField id="lndUsername"
                              label="Username"
                              name="lndUsername"
                              required={true}
                              value={spokeFormData.lndUsername.fieldValue}
                              readOnly={true}
                              disabled={true}
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex">
                    <Box
                      sx={{ border: 1, borderColor: "divider", padding: 3, width: "100%" }}
                    >
                      <h2>{constants.CONNECTION_TYPES.hstgspoke}</h2>
                      <Grid container spacing={4}>
                        <Grid item xs={4}>
                          <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                            <DataField id="historyEndpoint"
                              label="Endpoint"
                              name="historyEndpoint"
                              required={true}
                              value={spokeFormData.historyEndpoint.fieldValue}
                              readOnly={true}
                              disabled={true}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                            <DataField id="historyDatabase"
                              label="Database"
                              name="historyDatabase"
                              required={true}
                              value={spokeFormData.historyDatabase.fieldValue}
                              readOnly={true}
                              disabled={true}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                          <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                            <DataField id="historyPort"
                              label="Port"
                              name="historyPort"
                              required={true}
                              value={spokeFormData.historyPort.fieldValue}
                              readOnly={true}
                              disabled={true}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                          <FormControl sx={{ m: 1 }} fullWidth variant={"standard"}>
                            <DataField id="historyUsername"
                              label="Username"
                              name="historyUsername"
                              required={true}
                              value={spokeFormData.historyUsername.fieldValue}
                              readOnly={true}
                              disabled={true}
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )
          ) : (
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="0e01376d-c48c-4c94-a5d4-a8c2fb8ba120"
                    label="Name"
                    name="name"
                    required={true}
                    value={spokeFormData.name.fieldValue}
                    onChange={handleChange}
                    error={spokeFormData.name.isError}
                    disabled={mode === constants.MODE.view ? true : false}
                    readOnly={mode === constants.MODE.view ? true : false}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                {mode !== constants.MODE.view && (
                  <FormControl sx={{ m: 1 }} fullWidth variant={"outlined"}>
                    <Autocomplete
                      options={idpUsersProduction}
                      onChange={(event, value) => {
                        let v = value as IUser;
                        spokeFormData.owner.fieldValue =
                          v === null ? "" : v.userName;
                        setOwnerValue(v === null ? "" : v.userName);
                        if (ownerValue !== "")
                          spokeFormData.owner.isError = false;
                      }}
                      getOptionLabel={(option) =>
                        option === undefined ? " " : option.userName
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.userName === value.userName
                      }
                      value={functions.getIUser(spokeFormData.owner.fieldValue)}
                      disabled={mode === constants.MODE.view ? true : false}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="Owner"
                          label="Owner"
                          required
                          value={spokeFormData.owner.fieldValue}
                          error={spokeFormData.owner.isError}
                        />
                      )}
                    />
                  </FormControl>
                )}
              </Grid>

              <Grid item xs={3}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="252ec784-f899-4a30-9ca9-4f5c7247943d"
                    label="Admin AD Group Object ID"
                    name="adminGroupObjectId"
                    required={true}
                    value={spokeFormData.adminGroupObjectId.fieldValue}
                    onChange={handleChange}
                    error={spokeFormData.adminGroupObjectId.isError}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={3}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="0a28dd12-3947-4a9d-80d2-9934a3da867d"
                    label="Admin AD Group Name"
                    name="adminGroupName"
                    required={true}
                    value={spokeFormData.adminGroupName.fieldValue}
                    onChange={handleChange}
                    error={spokeFormData.adminGroupName.isError}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={3}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="9c2899f2-5665-47dc-a5c1-45af60664ca6"
                    label="User AD Group Object ID"
                    name="userGroupObjectId"
                    required={true}
                    value={spokeFormData.userGroupObjectId.fieldValue}
                    onChange={handleChange}
                    error={spokeFormData.userGroupObjectId.isError}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={3}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="05be9fa8-4a59-424d-92d7-d00db5949cbb"
                    label="User AD Group Name"
                    name="userGroupName"
                    required={true}
                    value={spokeFormData.userGroupName.fieldValue}
                    onChange={handleChange}
                    error={spokeFormData.userGroupName.isError}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex">
                  <ConnectionEXTSpoke
                    selectedConnection={constants.CONNECTION_TYPES.extSpoke}
                    onChange={handleChange}
                    accountKey={spokeFormData.accountKey}
                    accountName={spokeFormData.accountName}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex">
                  <ConnectionLNDSpoke
                    selectedConnection={constants.CONNECTION_TYPES.lndspoke}
                    onChange={handleChange}
                    lndEndpoint={spokeFormData.lndEndpoint}
                    lndDatabase={spokeFormData.lndDatabase}
                    lndPort={spokeFormData.lndPort}
                    lndUsername={spokeFormData.lndUsername}
                    lndPassword={spokeFormData.lndPassword}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex">
                  <ConnectionHistorySpoke
                    selectedConnection={constants.CONNECTION_TYPES.hstgspoke}
                    onChange={handleChange}
                    historyEndpoint={spokeFormData.historyEndpoint}
                    historyDatabase={spokeFormData.historyDatabase}
                    historyPort={spokeFormData.historyPort}
                    historyUsername={spokeFormData.historyUsername}
                    historyPassword={spokeFormData.historyPassword}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" m={1}>
                  {postConnectionPending && loadIcon ? (
                    <LoadingButton
                      loading
                      variant="outlined"
                      size="small"
                      loadingPosition="start"
                      startIcon={<SaveIcon />}
                    >
                      Processing
                    </LoadingButton>
                  ) : (
                    <Button id="a2cf5eaf-4c94-4e1c-964c-cadadbadc715"
                      color="primary"
                      variant="contained"
                      onClick={testConnection}
                      style={{ marginRight: "1em" }}
                    >
                      Test Connection
                    </Button>
                  )}
                  {displaySaveBtn &&
                    mode === constants.MODE.add &&
                    (postSpokePending && loadIcon ? (
                      <LoadingButton
                        loading
                        variant="outlined"
                        size="small"
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                      >
                        Processing
                      </LoadingButton>
                    ) : (
                      <Button id="d0f4db27-6fd5-498a-a8c6-a298e2e716f1"
                        color="primary"
                        variant="contained"
                        onClick={submitSpoke}
                        style={{ marginRight: "1em" }}
                      >
                        Register Spoke
                      </Button>
                    ))}
                  {backButton && (
                    <Button id="6559ab81-ce78-4818-bbd4-dfcc58ad0af4"
                      color="primary"
                      variant="contained"
                      onClick={goBack}
                      style={{ marginRight: "1em" }}
                    >
                      Back
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </Form>
      </div>
    </Formik>
  );
};
export default SpokeForm;
